import type { StateCreator } from 'zustand';
import type { TrendingTrack } from '../types';
import { trendingMusicApi } from '../services/trendingMusicApi';

export interface TrendingMusicSlice {
  tmTracks: TrendingTrack[];
  tmTotal: number;
  tmLoading: boolean;
  tmSyncing: boolean;
  tmPlatformFilter: string;
  tmSort: string;
  tmNicheFilter: string;
  tmMonthFilter: string;
  tmAvailableMonths: string[];
  tmLastSync: string | null;
  tmClassifying: boolean;
  tmClassifyProgress: { current: number; total: number } | null;
  tmClassifyError: string | null;
  tmAvailableNiches: string[];

  fetchTmTracks: () => Promise<void>;
  syncTmTracks: () => Promise<{ new_tracks: number; updated_tracks: number }>;
  addTmManualTrack: (url: string) => Promise<void>;
  updateTmTrack: (id: number, updates: Partial<TrendingTrack>) => Promise<void>;
  deleteTmTrack: (id: number) => Promise<void>;
  setTmPlatformFilter: (platform: string) => void;
  setTmSort: (sort: string) => void;
  setTmMonthFilter: (month: string) => void;
  classifyTmTracks: () => Promise<void>;
  setTmNicheFilter: (niche: string) => void;
  setTmClassifyError: (error: string | null) => void;
  fetchTmNiches: () => Promise<void>;
  fetchTmMonths: () => Promise<void>;
  fetchTmSyncStatus: () => Promise<void>;
  handleMusicClassifySSE: (data: Record<string, unknown>) => void;
}

export const createTrendingMusicSlice: StateCreator<TrendingMusicSlice, [], [], TrendingMusicSlice> = (set, get) => ({
  tmTracks: [],
  tmTotal: 0,
  tmLoading: false,
  tmSyncing: false,
  tmPlatformFilter: '',
  tmSort: 'trend_score',
  tmNicheFilter: '',
  tmMonthFilter: '',
  tmAvailableMonths: [],
  tmLastSync: null,
  tmClassifying: false,
  tmClassifyProgress: null,
  tmClassifyError: null,
  tmAvailableNiches: [],

  fetchTmTracks: async () => {
    set({ tmLoading: true });
    try {
      const { tmPlatformFilter, tmSort, tmNicheFilter, tmMonthFilter } = get();
      const result = await trendingMusicApi.listTracks({
        platform: tmPlatformFilter || undefined,
        sort: tmSort,
        limit: 100,
        niche: tmNicheFilter || undefined,
        month: tmMonthFilter || undefined,
      });
      set({ tmTracks: result.tracks, tmTotal: result.total });
    } finally {
      set({ tmLoading: false });
    }
  },

  syncTmTracks: async () => {
    set({ tmSyncing: true });
    try {
      const result = await trendingMusicApi.syncTracks(50);
      await get().fetchTmTracks();
      return result;
    } finally {
      set({ tmSyncing: false });
    }
  },

  addTmManualTrack: async (url: string) => {
    await trendingMusicApi.addManualTrack(url);
    await get().fetchTmTracks();
  },

  updateTmTrack: async (id: number, updates: Partial<TrendingTrack>) => {
    const updated = await trendingMusicApi.updateTrack(id, updates);
    set((s) => ({ tmTracks: s.tmTracks.map((t) => (t.id === id ? updated : t)) }));
  },

  deleteTmTrack: async (id: number) => {
    await trendingMusicApi.deleteTrack(id);
    set((s) => ({ tmTracks: s.tmTracks.filter((t) => t.id !== id) }));
  },

  setTmPlatformFilter: (platform: string) => {
    set({ tmPlatformFilter: platform });
  },

  setTmSort: (sort: string) => {
    set({ tmSort: sort });
  },

  classifyTmTracks: async () => {
    set({ tmClassifying: true, tmClassifyError: null });
    try {
      await trendingMusicApi.classifyTracks();
      // tmClassifying will be set to false by SSE complete event
    } catch (e: unknown) {
      set({
        tmClassifying: false,
        tmClassifyProgress: null,
        tmClassifyError: e instanceof Error ? e.message : 'Classification request failed',
      });
    }
  },

  setTmNicheFilter: (niche: string) => {
    set({ tmNicheFilter: niche });
  },

  setTmMonthFilter: (month: string) => {
    set({ tmMonthFilter: month });
  },

  fetchTmMonths: async () => {
    const result = await trendingMusicApi.getAvailableMonths();
    set({ tmAvailableMonths: result.months });
  },

  fetchTmSyncStatus: async () => {
    const result = await trendingMusicApi.getSyncStatus();
    set({ tmLastSync: result.last_sync });
  },

  setTmClassifyError: (error: string | null) => {
    set({ tmClassifyError: error });
  },

  fetchTmNiches: async () => {
    const result = await trendingMusicApi.listNiches();
    set({ tmAvailableNiches: result.niches });
  },

  handleMusicClassifySSE: (data: Record<string, unknown>) => {
    if (data.event === 'music_classify_progress') {
      set({
        tmClassifying: true,
        tmClassifyProgress: { current: (data.classified as number) ?? 0, total: (data.total as number) ?? 0 },
      });
    } else if (data.event === 'music_classify_complete') {
      const errors = data.errors as string[] | undefined;
      const failed = data.failed as number | undefined;
      const classified = data.classified as number;
      const total = data.total as number;

      let errorMsg: string | null = null;
      if (errors?.length) {
        errorMsg = `${classified}/${total} classified, ${failed} failed`;
      }
      set({ tmClassifying: false, tmClassifyProgress: null, tmClassifyError: errorMsg });
      // Reload tracks to show fresh niche badges
      get().fetchTmTracks();
      get().fetchTmNiches();
    }
  },
});
